// src/pages/SessionDetail/SessionDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { dataService } from '../../storage/dataService';
import { supabase } from '../../backend/supabaseClient';
import './SessionDetail.css';

function SessionDetail() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [pressureData, setPressureData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('avg_total');

  const metrics = [
    { key: 'avg_total', label: 'Total Average' },
    { key: 'avg_front', label: 'Front' },
    { key: 'avg_back', label: 'Back' },
    { key: 'avg_left', label: 'Left' },
    { key: 'avg_right', label: 'Right' },
    { key: 'avg_platform', label: 'Platform' },
    { key: 'avg_metatarsal', label: 'Metatarsal' }
  ];

  const loadSessionData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await dataService.getSessionData(sessionId);
      setPressureData(data);
      
      // Get session info
      const { data: sessionInfo, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .single();
      
      if (error) throw error;
      setSessionData(sessionInfo);
    } catch (error) {
      console.error('Error loading session data:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSessionData();
  }, [loadSessionData]);

  const prepareChartData = () => {
    const leftData = pressureData.filter(d => d.foot === 'left');
    const rightData = pressureData.filter(d => d.foot === 'right');

    return {
      labels: leftData.map(d => new Date(d.timestamp).toLocaleTimeString()),
      datasets: [
        {
          label: `Left Foot - ${metrics.find(m => m.key === selectedMetric)?.label}`,
          data: leftData.map(d => d[selectedMetric]),
          borderColor: '#3b82f6',
          backgroundColor: '#3b82f620',
          tension: 0.4,
          pointRadius: 0,
        },
        {
          label: `Right Foot - ${metrics.find(m => m.key === selectedMetric)?.label}`,
          data: rightData.map(d => d[selectedMetric]),
          borderColor: '#ef4444',
          backgroundColor: '#ef444420',
          tension: 0.4,
          pointRadius: 0,
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Pressure Over Time'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Pressure'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Time'
        },
        ticks: {
          maxTicksLimit: 10
        }
      }
    }
  };

  if (loading) {
    return <div className="loading">Loading session data...</div>;
  }

  return (
    <div className="session-detail-container">
      <button className="back-button" onClick={() => navigate('/my-data')}>
        ← Back to My Data
      </button>

      {sessionData && (
        <div className="session-header">
          <h1>{sessionData.session_name}</h1>
          <div className="session-info">
            <span>Date: {new Date(sessionData.created_at).toLocaleDateString()}</span>
            <span>Duration: {Math.floor(sessionData.duration_seconds / 60)} minutes</span>
            <span>Data Points: {pressureData.length / 2}</span>
          </div>
        </div>
      )}

      <div className="metric-selector">
        <label>Select Metric:</label>
        <select 
          value={selectedMetric} 
          onChange={(e) => setSelectedMetric(e.target.value)}
        >
          {metrics.map(metric => (
            <option key={metric.key} value={metric.key}>
              {metric.label}
            </option>
          ))}
        </select>
      </div>

      <div className="chart-container">
        <Line data={prepareChartData()} options={chartOptions} />
      </div>

      <div className="data-summary">
        <h2>Session Summary</h2>
        <div className="summary-grid">
          {metrics.map(metric => {
            const leftAvg = pressureData
              .filter(d => d.foot === 'left')
              .reduce((sum, d) => sum + d[metric.key], 0) / pressureData.filter(d => d.foot === 'left').length;
            const rightAvg = pressureData
              .filter(d => d.foot === 'right')
              .reduce((sum, d) => sum + d[metric.key], 0) / pressureData.filter(d => d.foot === 'right').length;

            return (
              <div key={metric.key} className="summary-card">
                <h3>{metric.label}</h3>
                <div className="summary-values">
                  <div>
                    <span className="label">Left:</span>
                    <span className="value">{leftAvg.toFixed(1)}</span>
                  </div>
                  <div>
                    <span className="label">Right:</span>
                    <span className="value">{rightAvg.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default SessionDetail;